import { useState, useCallback, useEffect } from "react";

export enum ConnectionStatus {
  SessionConnected,
  SessionDisconnected,
  SessionReconnected,
  SessionReconnecting,
  SubscriberConnected,
  SubscriberDisconnected,
  PublisherStreamCreated,
  PublisherStreamDestroyed,
  PublisherDenied,
  SubscriptionTimeout,
}

export interface OpenTokArgs {
  onStatusChange?: (status: ConnectionStatus) => void;
  createdAt: Date;
  timeout: number;
}

export const useOpenTok = ({
  onStatusChange,
  timeout,
  createdAt,
}: OpenTokArgs) => {
  const [connectionStatus, setConnectionStatus] = useState(
    ConnectionStatus.SessionDisconnected
  );
  const [publishVideo, setPublishVideo] = useState(true);
  const [waitingForParticipant, setWaitingForParticipant] = useState(true);
  const handleStatusChange = useCallback(
    (status: ConnectionStatus) => {
      switch (status) {
        case ConnectionStatus.SubscriberConnected:
          setWaitingForParticipant(false);
          break;
        case ConnectionStatus.SubscriberDisconnected:
          setWaitingForParticipant(true);
          break;
      }
      if (onStatusChange) {
        onStatusChange(status);
      }
    },
    [setWaitingForParticipant, onStatusChange]
  );

  const sessionEventHandlers = {
    sessionConnected: () => {
      setConnectionStatus(ConnectionStatus.SessionConnected);
    },
    sessionDisconnected: () => {
      setConnectionStatus(ConnectionStatus.SessionDisconnected);
    },
    sessionReconnected: () => {
      setConnectionStatus(ConnectionStatus.SessionReconnected);
    },
    sessionReconnecting: () => {
      setConnectionStatus(ConnectionStatus.SessionReconnecting);
    },
  };

  const publisherEventHandlers = {
    accessDenied: () => {
      setConnectionStatus(ConnectionStatus.PublisherDenied);
    },
    streamCreated: () => {
      setConnectionStatus(ConnectionStatus.PublisherStreamCreated);
    },
    streamDestroyed: () => {
      setConnectionStatus(ConnectionStatus.PublisherStreamDestroyed);
    },
  };

  const subscriberEventHandlers = {
    disconnected: () => {
      setConnectionStatus(ConnectionStatus.SubscriberDisconnected);
    },
    connected: () => {
      setConnectionStatus(ConnectionStatus.SubscriberConnected);
    },
  };

  const toggleVideo = useCallback(() => {
    setPublishVideo(!publishVideo);
  }, [setPublishVideo, publishVideo]);

  useEffect(() => {
    handleStatusChange(connectionStatus);
  }, [connectionStatus, onStatusChange, handleStatusChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const diff = new Date().getTime() - createdAt.getTime();
      if (Math.round(diff / 60000) >= timeout) {
        setConnectionStatus(ConnectionStatus.SubscriptionTimeout);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [createdAt, timeout]);

  return {
    sessionEventHandlers,
    toggleVideo,
    subscriberEventHandlers,
    publisherEventHandlers,
    publishVideo,
    waitingForParticipant,
  };
};
